import React, { Fragment, useState } from "react";
import { Plus, Search, MoreVertical } from "lucide-react";
import { Link } from "react-router-dom";
import { GREEN_CLOSE_BTN, NO_DATA, ARROW_LEFT } from "../../../assets";
import ModalV2 from "../../../shared/components/modalV2";
import SuccessModalV2 from "../../../shared/components/successModal/sucessModalV2";
import TableComponent from "../../../shared/components/table";
import { SortingState, createColumnHelper } from "@tanstack/react-table";
import { Checkbox } from "@mui/material";
import { Menu, Transition } from "@headlessui/react";
import RenameModal from "./case-folder/modals/RenameModal";
import WarningPromptModal from "./case-folder/modals/WarningPromptModal";

const officialTabs = [
  { id: "private_individuals", label: "Private  Individual (20)" },
  { id: "lawyers", label: "Lawyers" },
  { id: "registrar1_payment", label: "Registrar 1 payment" },
  { id: "registrar1_assessment", label: "Registrar 1 assessment" },
  { id: "registrar2", label: "Registrar 2 (30)" },
  { id: "panels", label: "Panel (4)" },
  { id: "president", label: "President" },
  { id: "baliff_it", label: "Baliff / IT staff" },
  { id: "police", label: "Police" },
  { id: "admin", label: "Admin" },
];

const courtStaffTabs = [
  { id: "registrar", label: "Court Registrar" },
  { id: "secretary", label: "Court Secretary" },
  { id: "clerk", label: "Court Clerk" },
  { id: "reporter", label: "Court Reporter" },
];

const designationOptions = [
  "Private Individual",
  "Lawyer",
  "Registrar 1 payment",
  "Registrar 1 assessment",
  "Registrar 2",
  "Panel",
  "President",
  "Baliff / IT staff",
  "Police",
  "Admin",
];

const judicialDivisionOptions = [
  "Enugu Division",
  "Nsukka Division",
  "Agbani Division",
];

const courtOptions = [
  "Hon. Justice J. Nnenna",
  "Hon. Justice T. Jacobe",
  "Hon. Justice J. Ekandem",
];

const courtStaffRoleOptions = [
  "Court Registrar",
  "Court Secretary",
  "Court Clerk",
  "Court Reporter",
];

const columnHelper = createColumnHelper<any>();

const OfficialsColumns = (
  onChangeRole: (user: any) => void,
  onRemoveUser: (user: any) => void,
) => [
  columnHelper.accessor("sn", {
    header: "SN",
    cell: ({ row }) => (
      <Checkbox
        color="success"
        checked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
        sx={{
          color: "#E0E0E0",
          "&.Mui-checked": {
            color: "#00B074",
          },
        }}
      />
    ),
  }),
  columnHelper.accessor("name", {
    header: "Name",
    cell: (info) => (
      <span className="text-[#101828] text-sm">{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor("email", {
    header: "Email",
    cell: (info) => (
      <span className="text-[#667085] text-sm">{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor("role", {
    header: "Role",
    cell: (info) => (
      <span
        className="text-[#101828] text-sm max-w-[200px] truncate block"
        title={info.getValue()}
      >
        {info.getValue()}
      </span>
    ),
  }),
  columnHelper.accessor("division", {
    header: "Judicial Division",
    cell: (info) => (
      <span className="text-[#667085] text-sm">{info.getValue()}</span>
    ),
  }),
  columnHelper.display({
    id: "action",
    header: "Action",
    cell: (info) => (
      <ActionMenu
        user={info.row.original}
        onChangeRole={onChangeRole}
        onRemoveUser={onRemoveUser}
      />
    ),
  }),
];

const ActionMenu = ({
  user,
  onChangeRole,
  onRemoveUser,
}: {
  user: any;
  onChangeRole: (user: any) => void;
  onRemoveUser: (user: any) => void;
}) => {
  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button className="p-2 text-[#98A2B3] hover:text-[#475467] transition-colors focus:outline-none">
        <MoreVertical className="w-5 h-5" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          anchor="bottom end"
          className="absolute right-0 mt-2 w-44 origin-top-right rounded-xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 py-2"
        >
          <div className="px-1 py-1 flex flex-col gap-y-1">
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => onChangeRole(user)}
                  className={`${
                    active ? "bg-[#D1FADF] text-[#027A48]" : "text-[#101828]"
                  } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm`}
                >
                  Change role
                </button>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => onRemoveUser(user)}
                  className={`${
                    active ? "bg-gray-50 text-[#B42318]" : "text-[#B42318]"
                  } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm`}
                >
                  Remove user
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

type CreateOfficialModalProps = {
  open: boolean;
  onClose: () => void;
  onContinue: () => void;
};

const CreateOfficialModal = ({
  open,
  onClose,
  onContinue,
}: CreateOfficialModalProps) => {
  const [fullName, setFullName] = useState("Amaka Obi");
  const [email, setEmail] = useState("G&G@gmail.com");
  const [designation, setDesignation] = useState("Registrar 2");
  const [division, setDivision] = useState("Enugu Division");
  const [phone, setPhone] = useState("+234 087654225678");

  return (
    <ModalV2
      isOpen={open}
      isClose={onClose}
      maxWidth="w-[450px]"
      edges="rounded-2xl"
    >
      <div className="px-8 py-8 flex flex-col items-end justify-end w-full gap-6">
        <button>
          <img src={GREEN_CLOSE_BTN} className="w-[17px] h-[17px]" alt="" />
        </button>
        <div className="w-full flex items-center justify-center flex-col">
          <span className="text-center text-xl font-semibold">
            Create an Official
          </span>
          <div className="w-full mt-3 flex flex-col items-start gap-y-6">
            <div className="flex flex-col gap-y-1 items-start w-full">
              <label
                htmlFor="title"
                className="text-sm font-normal text-[#98A2B3]"
              >
                Full Name
              </label>
              <input
                type="text"
                placeholder="Enter full name"
                className="w-full p-3 text-sm border border-[#CACACA] rounded-lg placeholder:text-[#98A2B3] text-customDark"
              />
            </div>
            <div className="flex flex-col gap-y-1 items-start w-full">
              <label
                htmlFor="title"
                className="text-sm font-normal text-[#98A2B3]"
              >
                Email address
              </label>
              <input
                type="text"
                placeholder="Enter email address"
                className="w-full p-3 text-sm border border-[#CACACA] rounded-lg placeholder:text-[#98A2B3] text-customDark"
              />
            </div>
            <div className="flex flex-col gap-y-1 items-start w-full">
              <label
                htmlFor="title"
                className="text-sm font-normal text-[#98A2B3]"
              >
                Designation
              </label>
              <select
                value={designation}
                onChange={(e) => setDesignation(e.target.value)}
                className="w-full p-3 text-sm border border-[#CACACA] rounded-lg bg-white text-customDark"
              >
                {designationOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-y-1 items-start w-full">
              <label
                htmlFor="title"
                className="text-sm font-normal text-[#98A2B3]"
              >
                Judicial Division
              </label>
              <select
                value={division}
                onChange={(e) => setDivision(e.target.value)}
                className="w-full p-3 text-sm border border-[#CACACA] rounded-lg bg-white text-customDark"
              >
                {judicialDivisionOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-y-1 items-start w-full">
              <label
                htmlFor="title"
                className="text-sm font-normal text-[#98A2B3]"
              >
                Telephone number
              </label>
              <input
                type="text"
                placeholder="Enter telephone number"
                className="w-full p-3 text-sm border border-[#CACACA] rounded-lg placeholder:text-[#98A2B3] text-customDark"
              />
            </div>
          </div>
        </div>

        <button
          onClick={onContinue}
          className="mt-2 w-full h-11 rounded-lg bg-[#009444] text-white text-sm font-semibold"
        >
          Continue
        </button>
      </div>
    </ModalV2>
  );
};

type CreateOfficialSignatureModalProps = {
  open: boolean;
  onClose: () => void;
  onContinue: () => void;
};

const CreateOfficialSignatureModal = ({
  open,
  onClose,
  onContinue,
}: CreateOfficialSignatureModalProps) => {
  const [signatureAdded] = useState(false);

  return (
    <ModalV2
      isOpen={open}
      isClose={onClose}
      maxWidth="w-[450px]"
      edges="rounded-2xl"
    >
      <div className="px-8 py-8 flex flex-col gap-6">
        <div className="flex items-start justify-between">
          <div className="flex-1 flex items-center justify-center">
            <h2 className="text-xl font-semibold text-[#101828]">
              Create an Official
            </h2>
          </div>
          <button onClick={onClose}>
            <img src={GREEN_CLOSE_BTN} className="w-[17px] h-[17px]" alt="" />
          </button>
        </div>

        <div className="flex flex-col gap-6 w-full">
          <div className="flex flex-col gap-3 items-start">
            <label className="text-sm font-medium text-[#101828]">
              Upload Signature
            </label>
            <div className="border border-[#D0D5DD] rounded-2xl px-6 py-10 flex flex-col items-center justify-center gap-4 bg-white">
              <button className="px-6 py-2 rounded-lg border border-[#00B074] text-[#00B074] text-sm font-semibold bg-white">
                Signature
              </button>
              <span className="text-sm text-[#667085]">
                Click on the button and sign your virtual signature.
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-3 items-start w-full">
            <label className="text-sm font-medium text-[#101828]">
              Upload judiciary seals
            </label>
            <div className="border border-dashed border-[#D0D5DD] rounded-2xl px-6 py-6 flex flex-col gap-3 bg-white w-full">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#667085]">
                  Upload judiciary seals file
                </span>
                <button className="px-4 py-2 rounded-lg border border-[#D0D5DD] text-sm font-medium text-[#111827] bg-white">
                  Browse
                </button>
              </div>
              <span className="text-xs text-[#98A2B3]">
                PNG, JPG or JPEG, not more than 2MB.
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-3 items-start w-full">
            <label className="text-sm font-medium text-[#101828]">
              Upload registrar&apos;s stamps
            </label>
            <div className="border border-dashed w-full border-[#D0D5DD] rounded-2xl px-6 py-6 flex flex-col gap-3 bg-white">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#667085]">
                  Upload registrar&apos;s stamp file
                </span>
                <button className="px-4 py-2 rounded-lg border border-[#D0D5DD] text-sm font-medium text-[#111827] bg-white">
                  Browse
                </button>
              </div>
              <span className="text-xs text-[#98A2B3]">
                PNG, JPG or JPEG, not more than 2MB.
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-stretch gap-3 mt-2">
          <button
            onClick={onClose}
            className="w-full h-10 rounded-lg border border-[#D0D5DD] text-sm font-medium text-[#475467] bg-white"
          >
            Skip
          </button>
          <button
            onClick={onContinue}
            className="w-full h-10 rounded-lg bg-[#009444] text-white text-sm font-semibold disabled:opacity-60"
            // disabled={!signatureAdded}
          >
            Continue
          </button>
        </div>
      </div>
    </ModalV2>
  );
};

type AddCourtStaffStepOneModalProps = {
  open: boolean;
  onClose: () => void;
  onContinue: () => void;
};

const AddCourtStaffStepOneModal = ({
  open,
  onClose,
  onContinue,
}: AddCourtStaffStepOneModalProps) => {
  const [division, setDivision] = useState(judicialDivisionOptions[0]);
  const [court, setCourt] = useState("");

  return (
    <ModalV2
      isOpen={open}
      isClose={onClose}
      maxWidth="w-[450px]"
      edges="rounded-2xl"
    >
      <div className="px-8 py-8 flex flex-col gap-6">
        <div className="flex items-start justify-between">
          <div className="flex-1 flex items-center justify-center">
            <h2 className="text-xl font-semibold text-[#101828]">
              Add a new Court staff
            </h2>
          </div>
          <button onClick={onClose}>
            <img src={GREEN_CLOSE_BTN} className="w-[17px] h-[17px]" alt="" />
          </button>
        </div>

        <div className="flex flex-col gap-4 w-full">
          <div className="w-full">
            <select
              value={division}
              onChange={(e) => setDivision(e.target.value)}
              className="w-full p-3 text-sm border border-[#CACACA] rounded-lg bg-white text-customDark"
            >
              {judicialDivisionOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div className="w-full">
            <select
              value={court}
              onChange={(e) => setCourt(e.target.value)}
              className="w-full p-3 text-sm border border-[#CACACA] rounded-lg bg-white text-customDark"
            >
              <option value="">Select court</option>
              {courtOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          onClick={onContinue}
          className="mt-2 w-full h-11 rounded-lg bg-[#009444] text-white text-sm font-semibold"
        >
          Continue
        </button>
      </div>
    </ModalV2>
  );
};

type AddCourtStaffStepTwoModalProps = {
  open: boolean;
  onClose: () => void;
  onContinue: () => void;
};

const AddCourtStaffStepTwoModal = ({
  open,
  onClose,
  onContinue,
}: AddCourtStaffStepTwoModalProps) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");

  return (
    <ModalV2
      isOpen={open}
      isClose={onClose}
      maxWidth="w-[450px]"
      edges="rounded-2xl"
    >
      <div className="px-8 py-8 flex flex-col gap-6">
        <div className="flex items-start justify-between">
          <div className="flex-1 flex items-center justify-center">
            <h2 className="text-xl font-semibold text-[#101828]">
              Add a new Court staff
            </h2>
          </div>
          <button onClick={onClose}>
            <img src={GREEN_CLOSE_BTN} className="w-[17px] h-[17px]" alt="" />
          </button>
        </div>

        <div className="flex flex-col gap-4 w-full">
          <input
            type="text"
            placeholder="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="w-full p-3 text-sm border border-[#CACACA] rounded-lg placeholder:text-[#98A2B3] text-customDark"
          />
          <input
            type="text"
            placeholder="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="w-full p-3 text-sm border border-[#CACACA] rounded-lg placeholder:text-[#98A2B3] text-customDark"
          />
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full p-3 text-sm border border-[#CACACA] rounded-lg placeholder:text-[#98A2B3] text-customDark"
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full p-3 text-sm border border-[#CACACA] rounded-lg bg-white text-customDark"
          >
            <option value="">Role</option>
            {courtStaffRoleOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={onContinue}
          className="mt-2 w-full h-11 rounded-lg bg-[#009444] text-white text-sm font-semibold"
        >
          Continue
        </button>
      </div>
    </ModalV2>
  );
};

function ManageOfficials() {
  const [activeMainTab, setActiveMainTab] = useState<
    "officials" | "court_staff"
  >("officials");
  const [activeOfficialTab, setActiveOfficialTab] = useState(
    officialTabs[0].id,
  );
  const [activeCourtStaffTab, setActiveCourtStaffTab] = useState(
    courtStaffTabs[0].id,
  );
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [officials, setOfficials] = useState([
    {
      id: 1,
      name: "Esther Ochendo",
      email: "Esthero@gmail.com",
      role: "Registrar 1 Assessment",
      division: "Enugu Division",
    },
    {
      id: 2,
      name: "Esther Ochendo",
      email: "Esthero@gmail.com",
      role: "Registrar 1 Assessment",
      division: "Enugu Division",
    },
    {
      id: 3,
      name: "Esther Ochendo",
      email: "Esthero@gmail.com",
      role: "Registrar 1 Assessment",
      division: "Enugu Division",
    },
  ]);

  const [courtStaff, setCourtStaff] = useState([
    {
      id: 4,
      name: "John Doe",
      email: "johndoe@gmail.com",
      role: "Court Registrar",
      division: "Enugu Division",
    },
    {
      id: 5,
      name: "Jane Smith",
      email: "janesmith@gmail.com",
      role: "Court Secretary",
      division: "Nsukka Division",
    },
  ]);

  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showRoleChangedModal, setShowRoleChangedModal] = useState(false);
  const [showUserRemovedModal, setShowUserRemovedModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [selectedContext, setSelectedContext] = useState<
    "officials" | "court_staff"
  >("officials");
  const [roleName, setRoleName] = useState("");

  const [showCourtStaffStepOne, setShowCourtStaffStepOne] = useState(false);
  const [showCourtStaffStepTwo, setShowCourtStaffStepTwo] = useState(false);
  const [showCourtStaffSuccessModal, setShowCourtStaffSuccessModal] =
    useState(false);

  const currentSubTabs =
    activeMainTab === "officials" ? officialTabs : courtStaffTabs;
  const activeSubTabId =
    activeMainTab === "officials" ? activeOfficialTab : activeCourtStaffTab;

  const activeData = activeMainTab === "officials" ? officials : courtStaff;

  const filteredData = activeData.filter((item) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      item.name.toLowerCase().includes(term) ||
      item.email.toLowerCase().includes(term) ||
      item.role.toLowerCase().includes(term) ||
      item.division.toLowerCase().includes(term)
    );
  });

  const handleChangeRoleClick = (user: any) => {
    setSelectedUser(user);
    setSelectedContext(activeMainTab);
    setRoleName(user.role);
    setShowRenameModal(true);
  };

  const handleRemoveUserClick = (user: any) => {
    setSelectedUser(user);
    setSelectedContext(activeMainTab);
    setShowWarningModal(true);
  };

  const handleConfirmChangeRole = () => {
    if (selectedUser) {
      if (selectedContext === "officials") {
        setOfficials((prev) =>
          prev.map((item) =>
            item.id === selectedUser.id ? { ...item, role: roleName } : item,
          ),
        );
      } else {
        setCourtStaff((prev) =>
          prev.map((item) =>
            item.id === selectedUser.id ? { ...item, role: roleName } : item,
          ),
        );
      }
    }
    setShowRenameModal(false);
    setShowRoleChangedModal(true);
  };

  const handleConfirmRemoveUser = () => {
    if (selectedUser) {
      if (selectedContext === "officials") {
        setOfficials((prev) =>
          prev.filter((item) => item.id !== selectedUser.id),
        );
      } else {
        setCourtStaff((prev) =>
          prev.filter((item) => item.id !== selectedUser.id),
        );
      }
    }
    setShowWarningModal(false);
    setShowUserRemovedModal(true);
  };

  const handleSubTabClick = (id: string) => {
    if (activeMainTab === "officials") {
      setActiveOfficialTab(id);
    } else {
      setActiveCourtStaffTab(id);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-screen">
      <div className="bg-white">
        <div className="px-8 pt-6 pb-4 border-b flex items-center justify-between w-full border-[#E9EAEB]">
          <h1 className="text-[28px] font-bold text-[#1A1A1A]">Users</h1>
          <div className="flex items-center gap-3">
            <button
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#D0D5DD] bg-white text-sm font-medium text-[#111827]"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus className="w-4 h-4" />
              <span>Create an official</span>
            </button>
            <button
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#D0D5DD] bg-white text-sm font-medium text-[#111827]"
              onClick={() => setShowCourtStaffStepOne(true)}
            >
              <Plus className="w-4 h-4" />
              <span>Add a Court staff</span>
            </button>
          </div>
        </div>
        <div className="px-8 py-3 border-b border-[#E9EAEB] flex items-center justify-between">
          <div className="flex items-center gap-x-2 text-sm">
            <Link
              to="/admin/overview"
              className="text-[#98A2B3] hover:text-[#00B074]"
            >
              Home
            </Link>
            <img src={ARROW_LEFT} alt="" className="w-[14px] h-[14px]" />
            <span className="font-medium text-[#1A1A1A]">Users</span>
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-[#E9EAEB] px-8 pt-4">
        <div className="flex items-center gap-8 text-sm font-medium border-b border-[#E9EAEB]">
          <button
            className={`pb-3 border-b-2 ${
              activeMainTab === "officials"
                ? "border-[#111827] text-[#111827]"
                : "border-transparent text-[#667085]"
            }`}
            onClick={() => setActiveMainTab("officials")}
          >
            Officials
          </button>
          <button
            className={`pb-3 border-b-2 ${
              activeMainTab === "court_staff"
                ? "border-[#111827] text-[#111827]"
                : "border-transparent text-[#667085]"
            }`}
            onClick={() => setActiveMainTab("court_staff")}
          >
            Court staffs
          </button>
        </div>

        <div className="mt-4 overflow-x-auto">
          <div className="flex items-center gap-6 text-sm font-medium whitespace-nowrap w-max">
            {currentSubTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleSubTabClick(tab.id)}
                className={`pb-3 border-b-2 whitespace-nowrap ${
                  activeSubTabId === tab.id
                    ? "border-[#111827] text-[#111827]"
                    : "border-transparent text-[#667085]"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-col flex-1 bg-white">
        <div className="px-8 py-6 flex flex-col gap-4">
          <div className="relative w-full max-w-[320px]">
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-4 pr-10 py-2.5 rounded-lg border border-[#E5E7EB] focus:outline-none focus:border-[#111827] bg-white text-sm placeholder:text-[#9CA3AF]"
            />
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
          </div>
        </div>

        <div className="flex-1 px-8 pb-10">
          {filteredData.length ? (
            <div className="bg-white rounded-2xl border border-[#E5E7EB]">
              <TableComponent
                DATA={filteredData}
                // @ts-ignore
                COLUMNS={OfficialsColumns(
                  handleChangeRoleClick,
                  handleRemoveUserClick,
                )}
                sorting={sorting}
                setSorting={setSorting}
              />
              <div className="flex items-center justify-between px-6 py-3 border-t border-[#E5E7EB] text-sm text-[#667085]">
                <span>Page 1 of 10</span>
                <div className="flex items-center gap-3">
                  <button className="px-4 py-1.5 rounded-lg border border-[#E5E7EB] bg-white hover:bg-gray-50">
                    Previous
                  </button>
                  <button className="px-4 py-1.5 rounded-lg border border-[#E5E7EB] bg-white hover:bg-gray-50">
                    Next
                  </button>
                  <div className="flex items-center gap-2">
                    <span>Entries per page:</span>
                    <span className="font-medium">10</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-4 h-full">
              <img src={NO_DATA} className="w-[200px] h-[200px]" alt="" />
              <span className="text-sm text-[#101828]">
                {activeMainTab === "court_staff"
                  ? "You have not created any court staff"
                  : "You have not created any official"}
              </span>
            </div>
          )}
        </div>
      </div>

      <CreateOfficialModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onContinue={() => {
          setShowCreateModal(false);
          setShowSignatureModal(true);
        }}
      />

      <CreateOfficialSignatureModal
        open={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
        onContinue={() => {
          setShowSignatureModal(false);
          setShowSuccessModal(true);
        }}
      />

      <SuccessModalV2
        open={showSuccessModal}
        close={() => setShowSuccessModal(false)}
        message="Official created successfully"
      />
      <AddCourtStaffStepOneModal
        open={showCourtStaffStepOne}
        onClose={() => setShowCourtStaffStepOne(false)}
        onContinue={() => {
          setShowCourtStaffStepOne(false);
          setShowCourtStaffStepTwo(true);
        }}
      />
      <AddCourtStaffStepTwoModal
        open={showCourtStaffStepTwo}
        onClose={() => setShowCourtStaffStepTwo(false)}
        onContinue={() => {
          setShowCourtStaffStepTwo(false);
          setShowCourtStaffSuccessModal(true);
        }}
      />
      <SuccessModalV2
        open={showCourtStaffSuccessModal}
        close={() => setShowCourtStaffSuccessModal(false)}
        message="Staff added successfully"
      />
      <RenameModal
        isOpen={showRenameModal}
        onClose={() => setShowRenameModal(false)}
        name={roleName}
        setName={setRoleName}
        onClick={handleConfirmChangeRole}
      />
      <WarningPromptModal
        isOpen={showWarningModal}
        onClose={() => setShowWarningModal(false)}
        message="Are you sure you want to remove User?"
        onClick={handleConfirmRemoveUser}
      />
      <SuccessModalV2
        open={showRoleChangedModal}
        close={() => setShowRoleChangedModal(false)}
        message="Role changed successfully"
      />
      <SuccessModalV2
        open={showUserRemovedModal}
        close={() => setShowUserRemovedModal(false)}
        message="User removed successfully"
      />
    </div>
  );
}

export default ManageOfficials;
